import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type {
  CalendarFeedView,
  CreateCalendarFeedInput,
  ExternalEventView,
  SyncOutcome,
  UpdateCalendarFeedInput,
} from '@shared/schemas/calendar-sync';

/* The queries behind /admin/calendar-sync. ROADMAP.md §11.7. */

const ROOT = ['admin', 'calendar-sync'] as const;

function invalidate(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: ROOT });
}

export function useCalendarFeeds() {
  return useQuery({
    queryKey: [...ROOT, 'feeds'],
    queryFn: () => api.get<{ data: CalendarFeedView[] }>('/api/admin/calendar-feeds'),
  });
}

export function useFeedEvents(feedId: string | undefined) {
  return useQuery({
    queryKey: [...ROOT, 'events', feedId],
    queryFn: () =>
      api.get<{ data: ExternalEventView[] }>(`/api/admin/calendar-feeds/${feedId ?? ''}/events`),
    enabled: feedId !== undefined,
  });
}

export function useCreateFeed() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCalendarFeedInput) =>
      api.post<CalendarFeedView>('/api/admin/calendar-feeds', input),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateFeed(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCalendarFeedInput) =>
      api.patch<CalendarFeedView>(`/api/admin/calendar-feeds/${id}`, input),
    onSuccess: () => invalidate(client),
  });
}

export function useSyncFeed(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SyncOutcome>(`/api/admin/calendar-feeds/${id}/sync`),
    onSuccess: () => {
      void invalidate(client);
      // A sync creates and moves jobs; the jobs and calendar screens should
      // not show yesterday's answer after the office pressed the button.
      void client.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    },
  });
}
